import { useEffect } from 'react';

/**
 * Aplica, enquanto o app do comprador está montado, a identidade PWA da
 * incorporadora do slug: aponta o `<link rel="manifest">` para o manifest
 * dinâmico por slug (nome/cor/ícone da org), o `apple-touch-icon` para o ícone
 * dela (iOS usa o apple-touch-icon, não o manifest) e a `theme-color`. Ao sair
 * do app do comprador, restaura os valores da plataforma — assim o app instalado
 * de cada incorporadora carrega a marca dela, isolada das demais.
 */
export function useManifestComprador(slug: string | undefined, corMarca?: string | null): void {
  useEffect(() => {
    if (!slug || typeof document === 'undefined') return;
    const head = document.head;

    function aplicarLink(rel: string, href: string): { el: HTMLLinkElement; anterior: string | null; criado: boolean } {
      let el = head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      const criado = !el;
      const anterior = el?.getAttribute('href') ?? null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        head.appendChild(el);
      }
      el.setAttribute('href', href);
      return { el, anterior, criado };
    }

    const manifest = aplicarLink('manifest', `/api/comprador/${slug}/manifest.webmanifest`);
    const icone = aplicarLink('apple-touch-icon', `/api/comprador/${slug}/icon.png`);

    // theme-color: só sobrescreve quando a org tem uma cor válida.
    const cor = corMarca && /^#[0-9a-fA-F]{6}$/.test(corMarca) ? corMarca : null;
    let theme: HTMLMetaElement | null = head.querySelector('meta[name="theme-color"]');
    const themeAnterior = theme?.getAttribute('content') ?? null;
    if (cor) {
      if (!theme) {
        theme = document.createElement('meta');
        theme.setAttribute('name', 'theme-color');
        head.appendChild(theme);
      }
      theme.setAttribute('content', cor);
    }

    return () => {
      function restaurar(l: { el: HTMLLinkElement; anterior: string | null; criado: boolean }) {
        if (l.criado) l.el.remove();
        else if (l.anterior != null) l.el.setAttribute('href', l.anterior);
      }
      restaurar(manifest);
      restaurar(icone);
      if (cor && theme && themeAnterior != null) theme.setAttribute('content', themeAnterior);
    };
  }, [slug, corMarca]);
}
